/** @module @liquidefy/code-editor/filesystem/filesystem-analysis @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  ASTNode,
  ASTQuery,
  SymbolInfo,
  SymbolKind,
  Range,
  Position,
  LanguageId,
  EditorResult,
  DocumentEncoding,
  ByteOrderMark,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asLanguageId,
} from '../code-editor.js';

// ═══ §2 AST OPERATIONS — create, traverse, manipulate AST nodes ═══

/**
 * Create a new ASTNode with the given properties.
 */
export function createASTNode(
  type: string,
  range: Range,
  language: LanguageId,
  children: ASTNode[] = [],
  name?: string,
  value?: unknown
): ASTNode {
  return { type, range, language, children, name, value };
}

/**
 * Add a child node to a parent. Returns a new parent with the child appended.
 */
export function addChild(parent: ASTNode, child: ASTNode): ASTNode {
  return { ...parent, children: [...parent.children, child] };
}

/**
 * Remove a child at the given index. Returns a new parent without that child.
 */
export function removeChild(parent: ASTNode, childIndex: number): ASTNode {
  if (childIndex < 0 || childIndex >= parent.children.length) return parent;
  const children = [...parent.children];
  children.splice(childIndex, 1);
  return { ...parent, children };
}

/**
 * Find the first node matching the predicate (depth-first).
 */
export function findNode(root: ASTNode, predicate: (node: ASTNode) => boolean): ASTNode | undefined {
  if (predicate(root)) return root;
  for (const child of root.children) {
    const found = findNode(child, predicate);
    if (found) return found;
  }
  return undefined;
}

/**
 * Find all nodes matching the predicate.
 */
export function findAllNodes(root: ASTNode, predicate: (node: ASTNode) => boolean): ASTNode[] {
  const results: ASTNode[] = [];
  walkASTInternal(root, node => {
    if (predicate(node)) results.push(node);
  });
  return results;
}

type ASTVisitor = (node: ASTNode, depth: number) => boolean | void;

/**
 * Walk the AST depth-first. Return false from visitor to stop.
 */
export function walkAST(root: ASTNode, visitor: ASTVisitor): void {
  walkASTDepth(root, visitor, 0);
}

function walkASTDepth(node: ASTNode, visitor: ASTVisitor, depth: number): boolean {
  const result = visitor(node, depth);
  if (result === false) return false;
  for (const child of node.children) {
    if (!walkASTDepth(child, visitor, depth + 1)) return false;
  }
  return true;
}

function walkASTInternal(node: ASTNode, fn: (n: ASTNode) => void): void {
  fn(node);
  for (const child of node.children) {
    walkASTInternal(child, fn);
  }
}

/**
 * Build the chain of ancestor nodes from root down to the target node.
 */
export function parentChain(root: ASTNode, target: ASTNode): ASTNode[] {
  const chain: ASTNode[] = [];

  function search(node: ASTNode): boolean {
    chain.push(node);
    if (node === target) return true;
    for (const child of node.children) {
      if (search(child)) return true;
    }
    chain.pop();
    return false;
  }

  search(root);
  return chain;
}

/**
 * Compute the depth of a target node within the tree rooted at root.
 */
export function nodeDepth(root: ASTNode, target: ASTNode): number {
  const chain = parentChain(root, target);
  return chain.length > 0 ? chain.length - 1 : -1;
}

/**
 * Count all nodes in the AST.
 */
export function nodeCount(root: ASTNode): number {
  let count = 0;
  walkASTInternal(root, () => { count++; });
  return count;
}

/**
 * Collect all leaf nodes (nodes with no children).
 */
export function leafNodes(root: ASTNode): ASTNode[] {
  return findAllNodes(root, node => node.children.length === 0);
}

/**
 * Get the range of a node.
 */
export function nodeRange(node: ASTNode): Range {
  return node.range;
}

/**
 * Extract the text of a node from the source content using its range.
 */
export function nodeText(content: string, node: ASTNode): string {
  const lines = content.split('\n');
  const startLine = node.range.start.line;
  const endLine = node.range.end.line;

  if (startLine === endLine) {
    const line = lines[startLine] ?? '';
    return line.slice(node.range.start.column, node.range.end.column);
  }

  const result: string[] = [];
  for (let i = startLine; i <= endLine; i++) {
    const line = lines[i] ?? '';
    if (i === startLine) {
      result.push(line.slice(node.range.start.column));
    } else if (i === endLine) {
      result.push(line.slice(0, node.range.end.column));
    } else {
      result.push(line);
    }
  }
  return result.join('\n');
}


// ═══ §3 AST QUERIES — pattern matching, positional queries, siblings ═══

/**
 * Check if a node matches a basic query pattern.
 * Matches by type, name, and language fields when present in the query.
 */
export function matchQuery(node: ASTNode, query: ASTQuery): boolean {
  if (query.language && node.language !== query.language) return false;

  const pattern = query.pattern;
  if (!pattern) return true;

  if (pattern.startsWith('type:')) {
    return node.type === pattern.slice(5);
  }
  if (pattern.startsWith('name:')) {
    return node.name === pattern.slice(5);
  }

  return node.type === pattern || node.name === pattern;
}

/**
 * Find all nodes of a specific type.
 */
export function findByType(root: ASTNode, type: string): ASTNode[] {
  return findAllNodes(root, node => node.type === type);
}

/**
 * Find all nodes with a specific name.
 */
export function findByName(root: ASTNode, name: string): ASTNode[] {
  return findAllNodes(root, node => node.name === name);
}

/**
 * Find all nodes matching a SymbolKind (as stored in node.type).
 */
export function findByKind(root: ASTNode, kind: SymbolKind): ASTNode[] {
  return findAllNodes(root, node => node.type === kind);
}

/**
 * Get all nodes whose range overlaps the given range.
 */
export function nodesInRange(root: ASTNode, range: Range): ASTNode[] {
  return findAllNodes(root, node => rangesOverlap(node.range, range));
}

/**
 * Find the deepest node at the given position.
 */
export function nodeAtPosition(root: ASTNode, position: Position): ASTNode | undefined {
  let best: ASTNode | undefined;

  walkASTInternal(root, node => {
    if (positionInRange(position, node.range)) {
      best = node;
    }
  });

  return best;
}

/**
 * Find the smallest enclosing node of a given type at a position.
 */
export function enclosingNode(root: ASTNode, position: Position, type?: string): ASTNode | undefined {
  let best: ASTNode | undefined;

  walkASTInternal(root, node => {
    if (positionInRange(position, node.range)) {
      if (!type || node.type === type) {
        if (!best || isNarrower(node.range, best.range)) {
          best = node;
        }
      }
    }
  });

  return best;
}

/**
 * Get all sibling nodes of a target node within the tree.
 */
export function siblingNodes(root: ASTNode, node: ASTNode): ASTNode[] {
  const chain = parentChain(root, node);
  if (chain.length < 2) return [];
  const parent = chain[chain.length - 2]!;
  return parent.children.filter(c => c !== node);
}

/**
 * Get the next sibling of a node.
 */
export function nextSibling(root: ASTNode, node: ASTNode): ASTNode | undefined {
  const chain = parentChain(root, node);
  if (chain.length < 2) return undefined;
  const parent = chain[chain.length - 2]!;
  const idx = parent.children.indexOf(node);
  if (idx < 0 || idx >= parent.children.length - 1) return undefined;
  return parent.children[idx + 1];
}

/**
 * Get the previous sibling of a node.
 */
export function previousSibling(root: ASTNode, node: ASTNode): ASTNode | undefined {
  const chain = parentChain(root, node);
  if (chain.length < 2) return undefined;
  const parent = chain[chain.length - 2]!;
  const idx = parent.children.indexOf(node);
  if (idx <= 0) return undefined;
  return parent.children[idx - 1];
}

function positionInRange(pos: Position, range: Range): boolean {
  if (pos.line < range.start.line || pos.line > range.end.line) return false;
  if (pos.line === range.start.line && pos.column < range.start.column) return false;
  if (pos.line === range.end.line && pos.column > range.end.column) return false;
  return true;
}

function rangesOverlap(a: Range, b: Range): boolean {
  if (a.end.line < b.start.line) return false;
  if (a.start.line > b.end.line) return false;
  if (a.end.line === b.start.line && a.end.column <= b.start.column) return false;
  if (a.start.line === b.end.line && a.start.column >= b.end.column) return false;
  return true;
}

function isNarrower(a: Range, b: Range): boolean {
  const aLines = a.end.line - a.start.line;
  const bLines = b.end.line - b.start.line;
  if (aLines !== bLines) return aLines < bLines;
  const aCols = (a.end.line === a.start.line)
    ? a.end.column - a.start.column
    : a.end.column;
  const bCols = (b.end.line === b.start.line)
    ? b.end.column - b.start.column
    : b.end.column;
  return aCols < bCols;
}


// ═══ §4 CODE INTELLIGENCE — extract symbols, imports, exports, dependency graph ═══

/**
 * Extract SymbolInfo entries from an AST root.
 * Looks for named nodes of types: function, class, interface, variable, method, enum, constant.
 */
export function extractSymbols(root: ASTNode): SymbolInfo[] {
  const symbolTypes = new Set<string>([
    'function', 'class', 'interface', 'variable', 'method',
    'enum', 'constant', 'property', 'constructor', 'namespace', 'module',
  ]);

  const symbols: SymbolInfo[] = [];

  walkASTInternal(root, node => {
    if (node.name && symbolTypes.has(node.type)) {
      symbols.push({
        name: node.name,
        kind: node.type as SymbolKind,
        range: node.range,
        selectionRange: node.range,
        uri: '',
      });
    }
  });

  return symbols;
}

interface ImportInfo {
  readonly module: string;
  readonly names: string[];
}

/**
 * Extract import statements from source content by language.
 */
export function extractImports(content: string, language: LanguageId): ImportInfo[] {
  const lang = language as string;
  const results: ImportInfo[] = [];
  const lines = content.split('\n');

  if (lang === 'typescript' || lang === 'javascript' || lang === 'tsx' || lang === 'jsx') {
    const importRe = /import\s+(?:(?:type\s+)?(?:\{([^}]*)\}|(\w+))\s+from\s+)?['"]([^'"]+)['"]/g;

    for (const line of lines) {
      let match: RegExpExecArray | null;
      const lineRegex = new RegExp(importRe.source, importRe.flags);
      while ((match = lineRegex.exec(line)) !== null) {
        const namedImports = match[1]
          ? match[1].split(',').map(s => s.trim().split(/\s+as\s+/)[0]!).filter(Boolean)
          : [];
        const defaultImport = match[2] ? [match[2]] : [];
        const module = match[3]!;
        results.push({ module, names: [...defaultImport, ...namedImports] as string[] });
      }
    }

    const requireRe = /(?:const|let|var)\s+(?:\{([^}]*)\}|(\w+))\s*=\s*require\s*\(\s*['"]([^'"]+)['"]\s*\)/g;
    for (const line of lines) {
      let match: RegExpExecArray | null;
      const lineRegex = new RegExp(requireRe.source, requireRe.flags);
      while ((match = lineRegex.exec(line)) !== null) {
        const namedImports = match[1]
          ? match[1].split(',').map(s => s.trim()).filter(Boolean)
          : [];
        const defaultImport = match[2] ? [match[2]] : [];
        results.push({ module: match[3]!, names: [...defaultImport, ...namedImports] as string[] });
      }
    }
  } else if (lang === 'python') {
    const importRe = /^(?:from\s+([\w.]+)\s+import\s+(.+)|import\s+([\w.,\s]+))/;
    for (const line of lines) {
      const trimmed = line.trim();
      const match = trimmed.match(importRe);
      if (match) {
        if (match[1]) {
          const names = match[2]!.split(',').map(s => s.trim().split(/\s+as\s+/)[0]!).filter(Boolean);
          results.push({ module: match[1], names });
        } else if (match[3]) {
          const modules = match[3].split(',').map(s => s.trim().split(/\s+as\s+/)[0]!).filter(Boolean);
          for (const m of modules) results.push({ module: m, names: [] });
        }
      }
    }
  } else if (lang === 'rust') {
    const useRe = /use\s+([\w:]+)(?:::(?:\{([^}]+)\}|(\w+)))?;/g;
    for (const line of lines) {
      let match: RegExpExecArray | null;
      const lineRegex = new RegExp(useRe.source, useRe.flags);
      while ((match = lineRegex.exec(line)) !== null) {
        const module = match[1]!;
        const names = match[2]
          ? match[2].split(',').map(s => s.trim()).filter(Boolean)
          : match[3] ? [match[3]] : [];
        results.push({ module, names: names as string[] });
      }
    }
  } else if (lang === 'go') {
    const singleRe = /import\s+"([^"]+)"/;
    const blockRe = /import\s*\(/;
    let inBlock = false;

    for (const line of lines) {
      const trimmed = line.trim();
      if (inBlock) {
        if (trimmed === ')') { inBlock = false; continue; }
        const match = trimmed.match(/(?:\w+\s+)?"([^"]+)"/);
        if (match) results.push({ module: match[1]!, names: [] });
      } else {
        if (blockRe.test(trimmed)) { inBlock = true; continue; }
        const match = trimmed.match(singleRe);
        if (match) results.push({ module: match[1]!, names: [] });
      }
    }
  }

  return results;
}

/**
 * Extract export declarations from source content.
 */
export function extractExports(content: string, language: LanguageId): string[] {
  const lang = language as string;
  const exports: string[] = [];
  const lines = content.split('\n');

  if (lang === 'typescript' || lang === 'javascript' || lang === 'tsx' || lang === 'jsx') {
    const exportRe = /export\s+(?:default\s+)?(?:function|class|const|let|var|interface|type|enum)\s+(\w+)/;
    const namedExportRe = /export\s*\{([^}]+)\}/;

    for (const line of lines) {
      const match = line.match(exportRe);
      if (match) { exports.push(match[1]!); continue; }
      const namedMatch = line.match(namedExportRe);
      if (namedMatch) {
        const names = namedMatch[1]!.split(',').map(s => s.trim().split(/\s+as\s+/)[0]!).filter(Boolean);
        exports.push(...names);
      }
    }
  } else if (lang === 'python') {
    const allRe = /__all__\s*=\s*\[([^\]]+)\]/;
    const full = content;
    const match = full.match(allRe);
    if (match) {
      const names = match[1]!.match(/['"](\w+)['"]/g);
      if (names) exports.push(...names.map(n => n.replace(/['"]/g, '')));
    }
  }

  return exports;
}

/**
 * Extract function declarations from content.
 */
export function extractFunctions(content: string, language: LanguageId): string[] {
  const lang = language as string;
  const fns: string[] = [];
  const lines = content.split('\n');

  if (lang === 'typescript' || lang === 'javascript' || lang === 'tsx' || lang === 'jsx') {
    const re = /(?:export\s+)?(?:async\s+)?function\s+(\w+)/;
    const arrowRe = /(?:export\s+)?(?:const|let|var)\s+(\w+)\s*=\s*(?:async\s+)?\(/;
    for (const line of lines) {
      const match = line.match(re) ?? line.match(arrowRe);
      if (match) fns.push(match[1]!);
    }
  } else if (lang === 'python') {
    const re = /^(?:\s*)def\s+(\w+)\s*\(/;
    for (const line of lines) {
      const match = line.match(re);
      if (match) fns.push(match[1]!);
    }
  } else if (lang === 'rust') {
    const re = /(?:pub\s+)?(?:async\s+)?fn\s+(\w+)/;
    for (const line of lines) {
      const match = line.match(re);
      if (match) fns.push(match[1]!);
    }
  } else if (lang === 'go') {
    const re = /^func\s+(?:\([^)]+\)\s+)?(\w+)/;
    for (const line of lines) {
      const match = line.match(re);
      if (match) fns.push(match[1]!);
    }
  }

  return fns;
}

/**
 * Extract class declarations from content.
 */
export function extractClasses(content: string, language: LanguageId): string[] {
  const lang = language as string;
  const classes: string[] = [];
  const lines = content.split('\n');

  if (lang === 'typescript' || lang === 'javascript' || lang === 'tsx' || lang === 'jsx') {
    const re = /(?:export\s+)?(?:abstract\s+)?class\s+(\w+)/;
    for (const line of lines) {
      const match = line.match(re);
      if (match) classes.push(match[1]!);
    }
  } else if (lang === 'python') {
    const re = /^class\s+(\w+)/;
    for (const line of lines) {
      const match = line.match(re);
      if (match) classes.push(match[1]!);
    }
  } else if (lang === 'rust') {
    const re = /(?:pub\s+)?struct\s+(\w+)/;
    for (const line of lines) {
      const match = line.match(re);
      if (match) classes.push(match[1]!);
    }
  } else if (lang === 'go') {
    const re = /type\s+(\w+)\s+struct/;
    for (const line of lines) {
      const match = line.match(re);
      if (match) classes.push(match[1]!);
    }
  }

  return classes;
}

/**
 * Extract variable/constant declarations from content.
 */
export function extractVariables(content: string, language: LanguageId): string[] {
  const lang = language as string;
  const vars: string[] = [];
  const lines = content.split('\n');

  if (lang === 'typescript' || lang === 'javascript' || lang === 'tsx' || lang === 'jsx') {
    const re = /(?:export\s+)?(?:const|let|var)\s+(\w+)\s*[:=]/;
    for (const line of lines) {
      const match = line.match(re);
      if (match && !/=\s*(?:async\s+)?\(/.test(line)) vars.push(match[1]!);
    }
  } else if (lang === 'python') {
    const re = /^(\w+)\s*[:=]\s*(?!.*def\s|.*class\s)/;
    for (const line of lines) {
      const trimmed = line.trimStart();
      if (trimmed.startsWith('#')) continue;
      const match = trimmed.match(re);
      if (match && !match[1]!.startsWith('_') || match) {
        if (match) vars.push(match[1]!);
      }
    }
  }

  return vars;
}

/**
 * Build a file-level dependency graph from imports and exports.
 * Returns a map of file → files it depends on.
 */
export function dependencyGraph(
  imports: Map<string, ImportInfo[]>,
  exports: Map<string, string[]>
): Map<string, string[]> {
  const graph = new Map<string, string[]>();
  const exportIndex = new Map<string, string>();

  for (const [file, exported] of exports) {
    for (const name of exported) {
      exportIndex.set(name, file);
    }
  }

  for (const [file, fileImports] of imports) {
    const deps = new Set<string>();
    for (const imp of fileImports) {
      for (const name of imp.names) {
        const source = exportIndex.get(name);
        if (source && source !== file) deps.add(source);
      }
    }
    graph.set(file, Array.from(deps));
  }

  return graph;
}


// ═══ §5 MIME & ENCODING — detection, binary check, BOM ═══

/**
 * Detect MIME type from filename extension.
 */
export function detectMimeType(filename: string): string {
  const ext = filename.includes('.') ? '.' + filename.split('.').pop()!.toLowerCase() : '';
  return MIME_TYPES[ext] ?? 'application/octet-stream';
}

/**
 * Detect encoding from byte content. Checks BOM first, then heuristic.
 */
export function detectEncoding(bytes: Uint8Array): DocumentEncoding {
  const bom = detectBOM(bytes);
  if (bom === 'utf-8') return 'utf-8';
  if (bom === 'utf-16le') return 'utf-16le';
  if (bom === 'utf-16be') return 'utf-16be';

  let hasHighBytes = false;
  let isValidUtf8 = true;
  let i = 0;

  while (i < bytes.length && i < 8192) {
    const byte = bytes[i]!;
    if (byte === 0) return 'utf-16le';
    if (byte > 127) {
      hasHighBytes = true;
      const seqLen = byte < 0xC0 ? 0 : byte < 0xE0 ? 2 : byte < 0xF0 ? 3 : byte < 0xF8 ? 4 : 0;
      if (seqLen === 0) { isValidUtf8 = false; i++; continue; }
      for (let j = 1; j < seqLen; j++) {
        if (i + j >= bytes.length || (bytes[i + j]! & 0xC0) !== 0x80) {
          isValidUtf8 = false; break;
        }
      }
      i += seqLen;
    } else {
      i++;
    }
  }

  if (!hasHighBytes || isValidUtf8) return 'utf-8';
  return 'latin1';
}

/**
 * Check if a file is binary by examining sample bytes.
 */
export function isBinaryFile(bytes: Uint8Array, sampleSize: number = 8192): boolean {
  const limit = Math.min(bytes.length, sampleSize);
  let nullCount = 0;
  let controlCount = 0;

  for (let i = 0; i < limit; i++) {
    const byte = bytes[i]!;
    if (byte === 0) nullCount++;
    if (byte < 8 && byte !== 0) controlCount++;
  }

  if (nullCount > 0) return true;
  if (controlCount / limit > 0.1) return true;

  return false;
}

/**
 * Comprehensive mapping of file extensions to MIME types.
 */
export const MIME_TYPES: Record<string, string> = {
  '.ts': 'text/typescript',
  '.tsx': 'text/typescript-jsx',
  '.js': 'text/javascript',
  '.jsx': 'text/javascript-jsx',
  '.mjs': 'text/javascript',
  '.cjs': 'text/javascript',
  '.json': 'application/json',
  '.jsonc': 'application/json',
  '.html': 'text/html',
  '.htm': 'text/html',
  '.css': 'text/css',
  '.scss': 'text/scss',
  '.sass': 'text/sass',
  '.less': 'text/less',
  '.xml': 'text/xml',
  '.svg': 'image/svg+xml',
  '.md': 'text/markdown',
  '.mdx': 'text/mdx',
  '.yaml': 'text/yaml',
  '.yml': 'text/yaml',
  '.toml': 'text/toml',
  '.ini': 'text/ini',
  '.cfg': 'text/plain',
  '.conf': 'text/plain',
  '.py': 'text/x-python',
  '.pyi': 'text/x-python',
  '.rb': 'text/x-ruby',
  '.rs': 'text/x-rust',
  '.go': 'text/x-go',
  '.java': 'text/x-java',
  '.kt': 'text/x-kotlin',
  '.scala': 'text/x-scala',
  '.c': 'text/x-c',
  '.h': 'text/x-c',
  '.cpp': 'text/x-c++',
  '.hpp': 'text/x-c++',
  '.cs': 'text/x-csharp',
  '.swift': 'text/x-swift',
  '.php': 'text/x-php',
  '.lua': 'text/x-lua',
  '.sh': 'text/x-shellscript',
  '.bash': 'text/x-shellscript',
  '.zsh': 'text/x-shellscript',
  '.fish': 'text/x-shellscript',
  '.ps1': 'text/x-powershell',
  '.bat': 'text/x-batch',
  '.cmd': 'text/x-batch',
  '.sql': 'text/x-sql',
  '.graphql': 'text/x-graphql',
  '.gql': 'text/x-graphql',
  '.proto': 'text/x-protobuf',
  '.dockerfile': 'text/x-dockerfile',
  '.makefile': 'text/x-makefile',
  '.cmake': 'text/x-cmake',
  '.tf': 'text/x-terraform',
  '.hcl': 'text/x-hcl',
  '.svelte': 'text/x-svelte',
  '.vue': 'text/x-vue',
  '.astro': 'text/x-astro',
  '.txt': 'text/plain',
  '.log': 'text/plain',
  '.csv': 'text/csv',
  '.tsv': 'text/tab-separated-values',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.ico': 'image/x-icon',
  '.bmp': 'image/bmp',
  '.tiff': 'image/tiff',
  '.pdf': 'application/pdf',
  '.zip': 'application/zip',
  '.tar': 'application/x-tar',
  '.gz': 'application/gzip',
  '.bz2': 'application/x-bzip2',
  '.7z': 'application/x-7z-compressed',
  '.rar': 'application/x-rar-compressed',
  '.wasm': 'application/wasm',
  '.woff': 'font/woff',
  '.woff2': 'font/woff2',
  '.ttf': 'font/ttf',
  '.otf': 'font/otf',
  '.eot': 'application/vnd.ms-fontobject',
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.ogg': 'audio/ogg',
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.avi': 'video/x-msvideo',
  '.mov': 'video/quicktime',
};

/**
 * Set of extensions known to be binary.
 */
export const BINARY_EXTENSIONS: ReadonlySet<string> = new Set([
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.ico', '.bmp', '.tiff',
  '.pdf', '.zip', '.tar', '.gz', '.bz2', '.7z', '.rar',
  '.wasm', '.woff', '.woff2', '.ttf', '.otf', '.eot',
  '.mp3', '.wav', '.ogg', '.mp4', '.webm', '.avi', '.mov',
  '.exe', '.dll', '.so', '.dylib', '.o', '.a',
  '.class', '.jar', '.pyc', '.pyo',
  '.sqlite', '.db', '.bin',
]);

/**
 * Set of extensions known to be text.
 */
export const TEXT_EXTENSIONS: ReadonlySet<string> = new Set([
  '.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs',
  '.json', '.jsonc', '.html', '.htm', '.css', '.scss', '.sass', '.less',
  '.xml', '.svg', '.md', '.mdx', '.yaml', '.yml', '.toml', '.ini',
  '.py', '.pyi', '.rb', '.rs', '.go', '.java', '.kt', '.scala',
  '.c', '.h', '.cpp', '.hpp', '.cs', '.swift', '.php', '.lua',
  '.sh', '.bash', '.zsh', '.fish', '.ps1', '.bat', '.cmd',
  '.sql', '.graphql', '.gql', '.proto',
  '.txt', '.log', '.csv', '.tsv', '.env',
  '.svelte', '.vue', '.astro',
  '.dockerfile', '.makefile', '.cmake', '.tf', '.hcl',
  '.gitignore', '.gitattributes', '.editorconfig',
]);

/**
 * Detect byte order mark from the first bytes.
 */
export function detectBOM(bytes: Uint8Array): ByteOrderMark {
  if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
    return 'utf-8';
  }
  if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) {
    return 'utf-16le';
  }
  if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) {
    return 'utf-16be';
  }
  return 'none';
}

/**
 * Human-readable label for an encoding.
 */
export function encodingLabel(encoding: DocumentEncoding): string {
  const labels: Record<DocumentEncoding, string> = {
    'utf-8': 'UTF-8',
    'utf-16le': 'UTF-16 LE',
    'utf-16be': 'UTF-16 BE',
    'utf-32': 'UTF-32',
    'latin1': 'Latin-1 (ISO 8859-1)',
    'shift-jis': 'Shift JIS',
    'euc-jp': 'EUC-JP',
    'iso-8859-1': 'ISO 8859-1',
  };
  return labels[encoding] ?? encoding;
}


// ═══ §6 FILE ANALYSIS — complexity, summary, generated/minified detection ═══

interface FileAnalysis {
  readonly lines: number;
  readonly words: number;
  readonly chars: number;
  readonly symbols: SymbolInfo[];
  readonly imports: ImportInfo[];
  readonly complexity: number;
}

/**
 * Analyze a file's content: counts, symbols, imports, complexity.
 */
export function analyzeFile(content: string, language: LanguageId): FileAnalysis {
  const lines = content.split('\n').length;
  const words = content.split(/\s+/).filter(w => w.length > 0).length;
  const chars = content.length;
  const imports = extractImports(content, language);
  const complexity = fileComplexity(content);

  return { lines, words, chars, symbols: [], imports, complexity };
}

/**
 * Compute a simple cyclomatic complexity score.
 * Counts decision points: if, else if, for, while, switch, case, catch, &&, ||, ?.
 */
export function fileComplexity(content: string): number {
  let complexity = 1;
  const patterns = [
    /\bif\b/g,
    /\belse\s+if\b/g,
    /\bfor\b/g,
    /\bwhile\b/g,
    /\bswitch\b/g,
    /\bcase\b/g,
    /\bcatch\b/g,
    /&&/g,
    /\|\|/g,
    /\?\./g,
    /\?\s*:/g,
  ];

  for (const pattern of patterns) {
    const matches = content.match(pattern);
    if (matches) complexity += matches.length;
  }

  return complexity;
}

/**
 * Generate a one-line summary of a file based on its content and language.
 */
export function fileSummary(content: string, language: LanguageId): string {
  const lines = content.split('\n');
  const lineCount = lines.length;
  const fns = extractFunctions(content, language);
  const classes = extractClasses(content, language);
  const imports = extractImports(content, language);

  const parts: string[] = [`${lineCount} lines`];
  if (fns.length > 0) parts.push(`${fns.length} function${fns.length > 1 ? 's' : ''}`);
  if (classes.length > 0) parts.push(`${classes.length} class${classes.length > 1 ? 'es' : ''}`);
  if (imports.length > 0) parts.push(`${imports.length} import${imports.length > 1 ? 's' : ''}`);

  return parts.join(', ');
}

/**
 * Check if a file appears to be auto-generated by looking for common markers.
 */
export function isGeneratedFile(content: string): boolean {
  const markers = [
    'AUTO-GENERATED',
    'auto-generated',
    'Auto-Generated',
    'DO NOT EDIT',
    'do not edit',
    'Do not edit',
    'generated by',
    'Generated by',
    '@generated',
    '@auto-generated',
    'THIS FILE IS GENERATED',
    'Code generated by',
    'automatically generated',
  ];

  const header = content.slice(0, 2048);
  return markers.some(m => header.includes(m));
}

/**
 * Detect if content is minified code.
 * Heuristic: average line length > 200, or fewer than 5 lines for > 500 chars.
 */
export function isMinified(content: string): boolean {
  if (content.length < 100) return false;

  const lines = content.split('\n');
  if (lines.length <= 3 && content.length > 500) return true;

  const totalLength = lines.reduce((sum, l) => sum + l.length, 0);
  const avgLength = totalLength / lines.length;

  return avgLength > 200;
}

/**
 * Heuristic language detection from content when extension is unknown.
 */
export function estimateLanguage(content: string): LanguageId {
  const trimmed = content.trimStart();

  if (trimmed.startsWith('#!/usr/bin/env python') || trimmed.startsWith('#!/usr/bin/python')) {
    return asLanguageId('python');
  }
  if (trimmed.startsWith('#!/bin/bash') || trimmed.startsWith('#!/bin/sh') || trimmed.startsWith('#!/usr/bin/env bash')) {
    return asLanguageId('shellscript');
  }
  if (trimmed.startsWith('#!/usr/bin/env node') || trimmed.startsWith('#!/usr/bin/node')) {
    return asLanguageId('javascript');
  }
  if (trimmed.startsWith('#!/usr/bin/env ruby') || trimmed.startsWith('#!/usr/bin/ruby')) {
    return asLanguageId('ruby');
  }

  if (/^import\s+type\s/.test(trimmed) || /:\s*\w+(?:<[^>]+>)?\s*[=;]/.test(content)) {
    return asLanguageId('typescript');
  }
  if (/^import\s+\w+\s+from\s+['"]/.test(trimmed) || /^(?:const|let|var)\s/.test(trimmed)) {
    return asLanguageId('javascript');
  }
  if (/^(?:def |class |import |from )/.test(trimmed)) {
    return asLanguageId('python');
  }
  if (/^package\s+\w+/.test(trimmed) || /^func\s+/.test(trimmed)) {
    return asLanguageId('go');
  }
  if (/^(?:use |fn |mod |pub |struct |enum |impl |trait )/.test(trimmed)) {
    return asLanguageId('rust');
  }
  if (/^(?:public |private |protected |class |interface |package )/.test(trimmed)) {
    return asLanguageId('java');
  }
  if (/^<\?php/.test(trimmed)) {
    return asLanguageId('php');
  }
  if (/^<!DOCTYPE\s+html/i.test(trimmed) || /^<html/i.test(trimmed)) {
    return asLanguageId('html');
  }
  if (/^\s*\{[\s\S]*"/.test(trimmed)) {
    return asLanguageId('json');
  }

  return asLanguageId('plaintext');
}
